import { Image as AntImage } from "antd";
import { ReactNode, useCallback, useEffect, useState } from "react";

import { FileProviderFile } from "../lib/file-provider";
import { useFileProvider } from "../lib/hooks";

export type ImageResizeOptions = {
  w?: number;
  h?: number;
};

type ImageProps = {
  src?: string | null;
  file?: FileProviderFile;
  cloudKey?: string;
  width?: string | number;
  height?: string | number;
  fit: "fill" | "contain" | "cover" | "none";
  alt?: string;
  placeholder?: ReactNode;
  className?: string;
  loading?: "eager" | "lazy";
  resize?: ImageResizeOptions;
  /** use image preview (blob) if exists */
  usePreview?: boolean;
  previewVisible?: boolean;
  disablePreview?: boolean;
  onPreviewVisibleChange?: (visible: boolean) => void;
  onLoad?: () => void;
  onError?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
};

export const Image: React.FC<ImageProps> = ({
  fit,
  alt,
  file,
  height,
  width,
  src,
  cloudKey,
  placeholder,
  className,
  loading,
  resize,
  usePreview,
  previewVisible,
  disablePreview,
  onPreviewVisibleChange,
  onError,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onLoad,
}: ImageProps) => {
  const fileProvider = useFileProvider();
  const [success, setSuccess] = useState(true);
  const [url, setUrl] = useState(src ?? fileProvider.getUrl(file!));
  const [previewUrl, setPreviewUrl] = useState(url);
  const [_previewVisible, setPreviewVisible] = useState(false);

  const handlePreviewVisibleChange = useCallback((visible: boolean) => {
    setPreviewVisible(visible);
    onPreviewVisibleChange?.(visible);
  }, [onPreviewVisibleChange]);

  useEffect(() => {
    let url = src ?? (cloudKey
      ? fileProvider.getUrlByKey(cloudKey!)
      : fileProvider.getUrl(file!, usePreview ?? false));
    setPreviewUrl(url);
    if (url?.startsWith('http') && resize) {
      try {
        const parsedUrl = new URL(url);
        if (resize.h) parsedUrl.searchParams.append('d', `h${resize.h}`);
        else if (resize.w) parsedUrl.searchParams.append('d', `w${resize.w}`);
        url = parsedUrl.toString();
      } catch { }
    }
    setUrl(url);
  }, [file, src, resize, usePreview, cloudKey]);

  const handleError = useCallback(() => {
    setSuccess(false);
    onError?.();
  }, []);


  if ((!success || !url) && placeholder)
    return <>{placeholder}</>;

  return <AntImage
    loading={loading}
    src={url}
    width={width}
    height={height}
    style={{
      width: width,
      height: height,
      objectFit: fit
    }}
    alt={alt}
    onLoad={() => onLoad?.()}
    onError={() => handleError()}
    onMouseEnter={() => onMouseEnter?.()}
    onMouseLeave={() => onMouseLeave?.()}
    className={className}
    onClick={() => onClick?.()}
    preview={disablePreview
      ? false
      : previewVisible ? {
        visible: previewVisible ?? _previewVisible,
        src: previewUrl,
        onVisibleChange: handlePreviewVisibleChange
      } : {
        src: previewUrl,
      }}
  />;
};
